/**
 * Helper utilities for allocation breakdown tables
 * Shared between EditableAllocationBreakdownTable and ReconciliationBreakdownTable
 */

export interface DateLabel {
  date: string; // YYYY-MM-DD format
  label: string; // Display label (e.g., "Jan 2024")
}

/**
 * Format dates for display in breakdown tables
 * Converts YYYY-MM-DD dates to "Month Year" format (e.g., "Jan 2024")
 */
export function formatDatesForDisplay(dates: string[]): DateLabel[] {
  return dates.map((date) => {
    const dateObj = new Date(date);
    return {
      date,
      label: dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
    };
  });
}

/**
 * Extract column order from date labels for BreakdownTable
 * Returns array of labels in chronological order
 */
export function extractColumnOrderFromDateLabels(
  dateLabels: DateLabel[],
): string[] {
  return dateLabels.map(({ label }) => label);
}

/**
 * Extract date string from Date object in YYYY-MM-DD format
 * Uses UTC methods to avoid timezone issues
 */
export function formatDateToString(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Build a map key for ruleId|divisionId|date lookups
 * Handles null divisionId by converting to string "null"
 */
export function buildAllocationKey(
  ruleId: string,
  divisionId: string | null,
  date: string,
): string {
  return `${ruleId}|${divisionId ?? null}|${date}`;
}
